/**
 * ALIRA Shopify Theme JavaScript
 * Handles: Cart Drawer, Announcements, Product variants, Quantity selectors,
 * Mobile menu, Search, Filtering, Sorting, Thumbnails, Quick Add, Toast
 */

(function () {
  'use strict';

  window.AliraTheme = {
    init: function () {
      if (this._initialized) return;
      this._initialized = true;

      this.AnnouncementBar.init();
      this.MobileMenu.init();
      this.SearchOverlay.init();
      this.CartDrawer.init();
      this.ProductForm.init();
      this.QuantitySelector.init();
      this.QuickAdd.init();
      this.CollectionFilters.init();
      this.CollectionSort.init();
      this.ProductThumbnails.init();
      this.MarqueeBar.init();
      // --- New Features ---
      this.PredictiveSearch.init();
      this.Wishlist.init();
      this.StickyCartBar.init();
      this.NotifyForm.init();
      this.ScrollToTop.init();
      this.CookieBanner.init();
      this.SocialProof.init();
      this.ProductTabs.init();
      this.RecentlyViewed.init();
      this.NewsletterModal.init();
      this.ImageZoom.init();
      this.ShareButton.init();
      this.QuickView.init();
      this.ProductRecommendations.init();
      this.FreeShippingBar.init();
      this.GiftWrapping.init();
      this.ShippingCalculator.init();
      this.InventoryStatus.init();
      this.CountdownTimer.init();
      this.DynamicPriceFormat.init();
    },

    /* =====================================================================
       Announcement Bar Rotation
       ===================================================================== */
    AnnouncementBar: {
      init: function () {
        var sliders = document.querySelectorAll('[data-section-type="announcement-bar"] .announcement-bar__slider');
        sliders.forEach(function (slider) {
          var slides = slider.querySelectorAll('.announcement-bar__slide');
          if (slides.length <= 1) return;

          var autoRotate = slider.getAttribute('data-auto-rotate') === 'true';
          var speed = parseInt(slider.getAttribute('data-rotation-speed'), 10) || 5;
          var currentIndex = 0;

          function showSlide(index) {
            slides.forEach(function (s) { s.classList.remove('is-active'); });
            slides[index].classList.add('is-active');
          }

          if (autoRotate) {
            setInterval(function () {
              currentIndex = (currentIndex + 1) % slides.length;
              showSlide(currentIndex);
            }, speed * 1000);
          }

          var section = slider.closest('[data-section-type="announcement-bar"]');
          var prevBtn = section.querySelector('.announcement-bar__prev');
          var nextBtn = section.querySelector('.announcement-bar__next');

          if (prevBtn) {
            prevBtn.addEventListener('click', function () {
              currentIndex = (currentIndex - 1 + slides.length) % slides.length;
              showSlide(currentIndex);
            });
          }
          if (nextBtn) {
            nextBtn.addEventListener('click', function () {
              currentIndex = (currentIndex + 1) % slides.length;
              showSlide(currentIndex);
            });
          }
        });
      }
    },

    /* =====================================================================
       Marquee Bar
       ===================================================================== */
    MarqueeBar: {
      init: function () {
        // Marquee runs via CSS animation, no JS needed for basic scrolling.
        // This hook exists for potential pause-on-hover.
        var tracks = document.querySelectorAll('[data-marquee-track]');
        tracks.forEach(function (track) {
          track.addEventListener('mouseenter', function () {
            track.style.animationPlayState = 'paused';
          });
          track.addEventListener('mouseleave', function () {
            track.style.animationPlayState = 'running';
          });
        });
      }
    },

    /* =====================================================================
       Mobile Menu
       ===================================================================== */
    MobileMenu: {
      init: function () {
        var toggle = document.querySelector('[data-mobile-menu-toggle]');
        var menu = document.querySelector('[data-mobile-menu]');
        if (!toggle || !menu) return;

        toggle.addEventListener('click', function () {
          var isOpen = menu.classList.toggle('is-open');
          document.body.classList.toggle('overflow-hidden', isOpen);
          toggle.setAttribute('aria-expanded', isOpen);
        });

        // Close on link click
        menu.querySelectorAll('a').forEach(function (link) {
          link.addEventListener('click', function () {
            menu.classList.remove('is-open');
            document.body.classList.remove('overflow-hidden');
            toggle.setAttribute('aria-expanded', 'false');
          });
        });
      }
    },

    /* =====================================================================
       Search Overlay
       ===================================================================== */
    SearchOverlay: {
      init: function () {
        var searchToggle = document.querySelector('[data-search-toggle]');
        var overlay = document.querySelector('[data-search-overlay]');
        var closeBtn = document.querySelector('[data-search-close]');
        if (!searchToggle || !overlay) return;

        searchToggle.addEventListener('click', function () {
          overlay.classList.add('is-open');
          document.body.classList.add('overflow-hidden');
          var input = overlay.querySelector('input');
          if (input) input.focus();
        });

        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            overlay.classList.remove('is-open');
            document.body.classList.remove('overflow-hidden');
          });
        }

        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) {
            overlay.classList.remove('is-open');
            document.body.classList.remove('overflow-hidden');
          }
        });
      }
    },

    /* =====================================================================
       Cart Drawer (AJAX)
       ===================================================================== */
    CartDrawer: {
      drawer: null,

      init: function () {
        this.drawer = document.querySelector('[data-cart-drawer]');
        if (!this.drawer) return;

        var self = this;

        // Toggle buttons
        document.querySelectorAll('[data-cart-toggle]').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self.open();
          });
        });

        // Close buttons
        this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            self.close();
          });
        });

        // Quantity change
        this.drawer.addEventListener('click', function (e) {
          var quantityBtn = e.target.closest('[data-cart-quantity-change]');
          if (quantityBtn) {
            var line = parseInt(quantityBtn.getAttribute('data-line'), 10);
            var change = parseInt(quantityBtn.getAttribute('data-change'), 10);
            self.changeQuantity(line, change);
          }

          var removeBtn = e.target.closest('[data-cart-remove]');
          if (removeBtn) {
            var removeLine = parseInt(removeBtn.getAttribute('data-line'), 10);
            self.removeItem(removeLine);
          }
        });

        // Close on ESC
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.drawer.getAttribute('aria-hidden') === 'false') {
            self.close();
          }
        });
      },

      open: function () {
        if (!this.drawer) return;
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        // Focus trap: move focus into drawer (accessibility skill)
        this._previousFocus = document.activeElement;
        var firstFocusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      },

      close: function () {
        if (!this.drawer) return;
        this.drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        // Restore focus to trigger element (accessibility skill)
        if (this._previousFocus) this._previousFocus.focus();
      },

      refreshDrawer: function () {
        var self = this;
        // Use Shopify Section Rendering API to get fresh server-rendered cart drawer HTML
        fetch('/?sections=cart-drawer')
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            var html = sections['cart-drawer'];
            if (html) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              var newDrawer = tmp.querySelector('[data-cart-drawer]');
              if (newDrawer && self.drawer) {
                // Replace body content
                var newBody = newDrawer.querySelector('[data-cart-drawer-body]');
                var oldBody = self.drawer.querySelector('[data-cart-drawer-body]');
                if (newBody && oldBody) oldBody.innerHTML = newBody.innerHTML;

                // Replace footer content
                var newFooter = newDrawer.querySelector('[data-cart-drawer-footer]');
                var oldFooter = self.drawer.querySelector('[data-cart-drawer-footer]');
                if (newFooter && oldFooter) {
                  oldFooter.innerHTML = newFooter.innerHTML;
                  oldFooter.style.display = '';
                } else if (!newFooter && oldFooter) {
                  oldFooter.style.display = 'none';
                } else if (newFooter && !oldFooter) {
                  // Footer didn't exist before (was empty cart), append it
                  var panel = self.drawer.querySelector('.cart-drawer__panel');
                  if (panel) panel.appendChild(newFooter.cloneNode(true));
                }

                // Update count
                var newCount = newDrawer.querySelector('[data-cart-drawer-count]');
                var oldCount = self.drawer.querySelector('[data-cart-drawer-count]');
                if (newCount && oldCount) oldCount.textContent = newCount.textContent;

                // Re-bind close buttons on new content
                self.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
                  btn.addEventListener('click', function () { self.close(); });
                });

                // Format INR prices in refreshed cart drawer
                self.drawer.querySelectorAll('.js-inr').forEach(function (el) {
                  var v = parseInt(el.getAttribute('data-inr'));
                  if (!isNaN(v)) el.textContent = '\u20B9 ' + v.toLocaleString('en-IN');
                });
              }
            }
            // Also update header cart count
            return fetch(window.routes.cart_url + '.js', { credentials: 'same-origin' });
          })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            self.updateCartCount(cart.item_count);
          })
          .catch(function (err) {
            console.error('Cart refresh error:', err);
          });
      },

      updateCartCount: function (count) {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          el.textContent = count;
          el.setAttribute('aria-label', count + ' items in cart');
        });
      },

      changeQuantity: function (line, change) {
        var self = this;
        var item = this.drawer.querySelector('[data-line="' + line + '"]');
        if (!item) return;

        var quantityEl = item.querySelector('[data-cart-item-quantity]');
        var currentQty = parseInt(quantityEl ? quantityEl.textContent : '1', 10);
        var newQty = Math.max(0, currentQty + change);

        var body = JSON.stringify({
          line: line,
          quantity: newQty
        });

        fetch(window.routes.cart_change_url + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: body
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            self.refreshDrawer();
            self.updateCartCount(cart.item_count);
          })
          .catch(function (err) {
            console.error('Quantity change error:', err);
          });
      },

      removeItem: function (line) {
        this.changeQuantity(line, -9999);
      },

      addToCart: function (variantId, quantity) {
        var self = this;
        quantity = quantity || 1;

        var body = JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        });

        return fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: body
        })
          .then(function (r) {
            if (!r.ok) throw new Error('Add to cart failed');
            return r.json();
          })
          .then(function () {
            self.refreshDrawer();
            self.open();
            AliraTheme.Toast.show(window.cartStrings.addedToCart || 'Added to cart!', 'success');
          })
          .catch(function (err) {
            console.error('Add to cart error:', err);
            AliraTheme.Toast.show(window.cartStrings.addToCartError || 'Could not add to cart', 'error');
          });
      },

      formatMoney: function (cents) {
        var rupees = Math.round(cents / 100);
        return '₹ ' + rupees.toLocaleString('en-IN');
      }
    },

    /* =====================================================================
       Product Form (variant selection + add to cart)
       ===================================================================== */
    ProductForm: {
      init: function () {
        var forms = document.querySelectorAll('[data-product-form]');
        forms.forEach(function (form) {
          var productJsonEl = form.closest('.product-details').querySelector('[data-product-json]');
          if (!productJsonEl) return;

          var product;
          try {
            product = JSON.parse(productJsonEl.textContent);
          } catch (e) {
            return;
          }

          // Note: inventory_policy is only available via Admin API, not the storefront
          // /products/{handle}.js endpoint. Availability is determined solely by
          // the 'available' field which Shopify already accounts for.

          var optionSelects = form.querySelectorAll('[data-option-select]');
          var variantInput = form.querySelector('[data-variant-id]');
          var addToCartBtn = form.querySelector('[data-add-to-cart]');
          var addToCartText = form.querySelector('[data-add-to-cart-text]');
          var priceContainer = form.closest('.product-details').querySelector('[data-product-price]');

          function getSelectedOptions() {
            var options = [];
            optionSelects.forEach(function (select) {
              options.push(select.value);
            });
            return options;
          }

          function findVariant(options) {
            return product.variants.find(function (variant) {
              return variant.options.every(function (opt, i) {
                return opt === options[i];
              });
            });
          }

          function updateVariant() {
            var options = getSelectedOptions();
            var variant = findVariant(options);

            if (variant) {
              variantInput.value = variant.id;

              // Check if dynamic pricing is active (skip price update if so)
              var isDynamic = priceContainer && priceContainer.querySelector('.dynamic-price');

              // Update price (only for non-dynamic-priced products)
              if (priceContainer && !isDynamic) {
                var priceHtml = '<span class="product-details__price-current">' + AliraTheme.CartDrawer.formatMoney(variant.price) + '</span>';
                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                  priceHtml += '<span class="product-details__price-compare">' + AliraTheme.CartDrawer.formatMoney(variant.compare_at_price) + '</span>';
                  var discount = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
                  priceHtml += '<span class="product-details__price-badge">-' + discount + '%</span>';
                }
                priceContainer.innerHTML = priceHtml;
              }

              // Update add to cart button
              if (variant.available) {
                addToCartBtn.disabled = false;
                if (addToCartText) {
                  // Preserve dynamic price button text if present
                  if (addToCartText.classList.contains('js-inr-btn')) {
                    var label = addToCartText.getAttribute('data-label') || window.cartStrings.addToCart || 'Add to Bag';
                    var inrVal = addToCartText.getAttribute('data-inr');
                    if (inrVal) {
                      addToCartText.textContent = label + ' - \u20B9 ' + parseInt(inrVal).toLocaleString('en-IN');
                    }
                  } else {
                    addToCartText.textContent = addToCartText.getAttribute('data-label') || window.cartStrings.addToCart || 'Add to Bag';
                    addToCartText.textContent += ' - ' + AliraTheme.CartDrawer.formatMoney(variant.price);
                  }
                }
              } else {
                addToCartBtn.disabled = true;
                if (addToCartText) {
                  addToCartText.textContent = addToCartText.getAttribute('data-sold-out') || window.cartStrings.soldOut || 'Sold Out';
                }
              }

              // Update URL
              var url = new URL(window.location);
              url.searchParams.set('variant', variant.id);
              window.history.replaceState({}, '', url);

              // Update SKU
              var skuEl = document.querySelector('[data-product-sku]');
              if (skuEl) {
                skuEl.textContent = variant.sku || '';
              }
            }
          }

          optionSelects.forEach(function (select) {
            select.addEventListener('change', updateVariant);
          });

          // Hydrate availability from Shopify AJAX API to bypass stale HTML page cache.
          // Fixes old products stuck showing "Sold Out" after inventory/policy changes in admin.
          var productHandle = form.getAttribute('data-product-handle');
          if (productHandle) {
            fetch('/products/' + productHandle + '.js')
              .then(function (r) { return r.json(); })
              .then(function (fresh) {
                // Sync fresh availability from uncached AJAX endpoint into in-memory variants.
                // inventory_policy is NOT available from the storefront endpoint — rely on 'available'.
                fresh.variants.forEach(function (freshV) {
                  var cached = product.variants.find(function (v) { return v.id === freshV.id; });
                  if (cached) cached.available = freshV.available;
                });

                // Determine the currently selected variant
                var currentId = parseInt(variantInput.value, 10);
                var freshVariant = fresh.variants.find(function (v) { return v.id === currentId; });
                if (!freshVariant) return;

                // Update main add-to-cart button based on fresh availability.
                // Do NOT update the price display here — the Liquid-rendered price
                // is always the most accurate (metafields + gold rate at render time).
                // Overwriting with freshVariant.price would show the stale Shopify
                // variant price for products whose metafields changed since last sync.
                if (freshVariant.available) {
                  addToCartBtn.disabled = false;
                  if (addToCartText) {
                    var label = addToCartText.getAttribute('data-label') || window.cartStrings.addToCart || 'Add to Bag';
                    if (addToCartText.classList.contains('js-inr-btn')) {
                      var inr = addToCartText.getAttribute('data-inr');
                      addToCartText.textContent = inr ? label + ' - \u20B9 ' + parseInt(inr).toLocaleString('en-IN') : label;
                    } else {
                      addToCartText.textContent = label + ' - ' + AliraTheme.CartDrawer.formatMoney(freshVariant.price);
                    }
                  }
                } else {
                  addToCartBtn.disabled = true;
                  if (addToCartText) {
                    addToCartText.textContent = addToCartText.getAttribute('data-sold-out') || window.cartStrings.soldOut || 'Sold Out';
                  }
                }

                // Update sticky cart bar button
                var stickyBtn = document.querySelector('[data-sticky-add-to-cart]');
                if (stickyBtn) {
                  stickyBtn.disabled = !freshVariant.available;
                  stickyBtn.textContent = freshVariant.available
                    ? (stickyBtn.getAttribute('data-label') || window.cartStrings.addToCart || 'Add to Cart')
                    : (stickyBtn.getAttribute('data-sold-out') || window.cartStrings.soldOut || 'Sold Out');
                }
              })
              .catch(function () { /* silent fail — keep Liquid-rendered state */ });
          }

          // Handle form submission via AJAX
          form.addEventListener('submit', function (e) {
            e.preventDefault();
            var variantId = parseInt(variantInput.value, 10);
            var quantityInput = form.querySelector('[data-quantity-input]');
            var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

            addToCartBtn.disabled = true;
            addToCartBtn.classList.add('is-loading');

            AliraTheme.CartDrawer.addToCart(variantId, quantity)
              .finally(function () {
                addToCartBtn.disabled = false;
                addToCartBtn.classList.remove('is-loading');
              });
          });
        });
      }
    },

    /* =====================================================================
       Quantity Selector (global)
       ===================================================================== */
    QuantitySelector: {
      init: function () {
        document.addEventListener('click', function (e) {
          var minusBtn = e.target.closest('[data-quantity-minus]');
          var plusBtn = e.target.closest('[data-quantity-plus]');

          if (minusBtn) {
            var input = minusBtn.closest('.product-details__quantity, .quantity-selector').querySelector('[data-quantity-input]');
            if (input) {
              var val = parseInt(input.value, 10) || 1;
              input.value = Math.max(1, val - 1);
              input.dispatchEvent(new Event('change'));
            }
          }

          if (plusBtn) {
            var inputPlus = plusBtn.closest('.product-details__quantity, .quantity-selector').querySelector('[data-quantity-input]');
            if (inputPlus) {
              var valPlus = parseInt(inputPlus.value, 10) || 1;
              inputPlus.value = valPlus + 1;
              inputPlus.dispatchEvent(new Event('change'));
            }
          }
        });

        // Cart page quantity changes
        document.addEventListener('click', function (e) {
          var changeBtn = e.target.closest('[data-quantity-change]');
          if (changeBtn && !changeBtn.closest('[data-cart-drawer]')) {
            var line = parseInt(changeBtn.getAttribute('data-line'), 10);
            var direction = parseInt(changeBtn.getAttribute('data-quantity-change'), 10);
            var input = changeBtn.closest('.quantity-selector').querySelector('.quantity-selector__input');
            if (input) {
              var current = parseInt(input.value, 10) || 1;
              var newVal = Math.max(0, current + direction);
              input.value = newVal;
            }
          }
        });
      }
    },

    /* =====================================================================
       Quick Add to Cart
       ===================================================================== */
    QuickAdd: {
      init: function () {
        document.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-quick-add]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();

          var variantId = parseInt(btn.getAttribute('data-quick-add'), 10);
          if (!variantId) return;

          btn.disabled = true;
          btn.innerHTML = '<span class="loading-spinner"></span>';

          AliraTheme.CartDrawer.addToCart(variantId, 1)
            .finally(function () {
              btn.disabled = false;
              btn.innerHTML = '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 01-8 0"></path></svg>';
            });
        });
      }
    },

    /* =====================================================================
       Product Image Thumbnails
       ===================================================================== */
    ProductThumbnails: {
      init: function () {
        document.querySelectorAll('[data-thumbnail]').forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            var mediaRoot = thumb.closest('.product-details__media');
            var mainImage = mediaRoot ? mediaRoot.querySelector('.product-details__image') : null;
            if (!mainImage) return;

            var src = thumb.getAttribute('data-image-src');
            var srcset = thumb.getAttribute('data-image-srcset');
            var alt = thumb.getAttribute('data-image-alt');

            if (src) mainImage.src = src;
            if (srcset) mainImage.srcset = srcset;
            if (alt) mainImage.alt = alt;

            // Update active state
            mediaRoot.querySelectorAll('[data-thumbnail]').forEach(function (t) {
              t.classList.remove('is-active');
            });
            thumb.classList.add('is-active');
          });
        });
      }
    },

    /* =====================================================================
       Collection Filters
       ===================================================================== */
    CollectionFilters: {
      init: function () {
        var toggle = document.querySelector('[data-filter-toggle]');
        var panel = document.querySelector('[data-filter-panel]');
        if (!toggle || !panel) return;

        toggle.addEventListener('click', function () {
          panel.classList.toggle('is-open');
        });

        // Handle filter form submission
        var form = panel.querySelector('.collection-grid__filter-form');
        if (form) {
          form.addEventListener('submit', function (e) {
            e.preventDefault();
            var formData = new FormData(form);
            var params = new URLSearchParams();

            for (var pair of formData.entries()) {
              params.append(pair[0], pair[1]);
            }

            var url = window.location.pathname + '?' + params.toString();
            window.location.href = url;
          });
        }
      }
    },

    /* =====================================================================
       Collection Sort
       ===================================================================== */
    CollectionSort: {
      init: function () {
        var select = document.querySelector('[data-sort-select]');
        if (!select) return;

        select.addEventListener('change', function () {
          var url = new URL(window.location);
          url.searchParams.set('sort_by', select.value);
          window.location.href = url.toString();
        });
      }
    },

    /* =====================================================================
       1. Predictive Search
       ===================================================================== */
    PredictiveSearch: {
      init: function () {
        var searchInputs = document.querySelectorAll('[data-search-input]');
        if (!searchInputs.length) return;
        var self = this;
        searchInputs.forEach(function (input) {
          var wrapper = input.closest('.search-form-wrapper');
          if (!wrapper) return;
          var dropdown = wrapper.querySelector('.predictive-search') || self.createDropdown(wrapper);
          var debounceTimer = null;
          var lastQuery = '';

          input.addEventListener('input', function () {
            clearTimeout(debounceTimer);
            var q = input.value.trim();
            if (q.length < 2) {
              dropdown.classList.remove('is-active');
              lastQuery = '';
              return;
            }
            if (q === lastQuery) return;

            debounceTimer = setTimeout(function () {
              lastQuery = q;
              self.fetchResults(q, dropdown);
            }, 500);
          });
          input.addEventListener('blur', function () {
            setTimeout(function () { dropdown.classList.remove('is-active'); }, 200);
          });
        });
      },
      createDropdown: function (wrapper) {
        var div = document.createElement('div');
        div.className = 'predictive-search';
        wrapper.appendChild(div);
        return div;
      },
      fetchResults: function (query, dropdown) {
        var url = (window.routes.predictive_search_url || '/search/suggest') + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var results = doc.querySelector('#predictive-search-results');
            if (results) {
              dropdown.innerHTML = results.innerHTML;
              dropdown.classList.add('is-active');
            } else {
              var emptyDiv = document.createElement('div');
              emptyDiv.className = 'predictive-search__empty';
              var noResultsMsg = (window.cartStrings.noSearchResults || 'No results for "{{ terms }}"').replace('{{ terms }}', query);
              emptyDiv.textContent = noResultsMsg;
              dropdown.innerHTML = '';
              dropdown.appendChild(emptyDiv);
              dropdown.classList.add('is-active');
            }
          })
          .catch(function () { dropdown.classList.remove('is-active'); });
      }
    },

    /* =====================================================================
       2. Wishlist (localStorage)
       ===================================================================== */
    Wishlist: {
      storageKey: 'alira_wishlist',
      init: function () {
        var self = this;
        self.syncButtons();
        document.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-wishlist-btn]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          var handle = btn.getAttribute('data-product-handle');
          if (self.has(handle)) {
            self.remove(handle);
            btn.classList.remove('is-wishlisted');
            btn.setAttribute('aria-pressed', 'false');
            self.updateCount();
            AliraTheme.Toast.show(window.cartStrings.removedFromWishlist || 'Removed from wishlist', 'success');
          } else {
            self.add({
              handle: handle,
              id: btn.getAttribute('data-product-id'),
              title: btn.getAttribute('data-product-title'),
              url: btn.getAttribute('data-product-url'),
              image: btn.getAttribute('data-product-image'),
              price: btn.getAttribute('data-product-price')
            });
            btn.classList.add('is-wishlisted');
            btn.setAttribute('aria-pressed', 'true');
            self.updateCount();
            AliraTheme.Toast.show(window.cartStrings.addedToWishlist || 'Added to wishlist', 'success');
          }
        });

        // Also handle old data-wishlist-toggle on product page
        document.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-wishlist-toggle]');
          if (!btn) return;
          var section = btn.closest('[data-product-id]');
          if (!section) return;
          var json = section.querySelector('[data-product-json]');
          if (!json) return;
          try {
            var prod = JSON.parse(json.textContent);
            if (self.has(prod.handle)) {
              self.remove(prod.handle);
              btn.classList.remove('is-wishlisted');
              btn.setAttribute('aria-pressed', 'false');
              self.updateCount();
              AliraTheme.Toast.show(window.cartStrings.removedFromWishlist || 'Removed from wishlist', 'success');
            } else {
              self.add({
                handle: prod.handle,
                id: '' + prod.id,
                title: prod.title,
                url: prod.url,
                image: prod.featured_image ? prod.featured_image : '',
                price: ''
              });
              btn.classList.add('is-wishlisted');
              btn.setAttribute('aria-pressed', 'true');
              self.updateCount();
              AliraTheme.Toast.show(window.cartStrings.addedToWishlist || 'Added to wishlist', 'success');
            }
          } catch (err) { /* ignore */ }
        });
      },
      getAll: function () {
        try { return JSON.parse(localStorage.getItem(this.storageKey)) || []; } catch (e) { return []; }
      },
      has: function (handle) {
        return this.getAll().some(function (p) { return p.handle === handle; });
      },
      add: function (product) {
        var list = this.getAll().filter(function (p) { return p.handle !== product.handle; });
        list.unshift(product);
        localStorage.setItem(this.storageKey, JSON.stringify(list));
      },
      remove: function (handle) {
        var list = this.getAll().filter(function (p) { return p.handle !== handle; });
        localStorage.setItem(this.storageKey, JSON.stringify(list));
      },
      updateCount: function () {
        var count = this.getAll().length;
        document.querySelectorAll('.wishlist-count').forEach(function (badge) {
          badge.textContent = count;
          badge.style.display = count > 0 ? '' : 'none';
        });
      },
      syncButtons: function () {
        var self = this;
        document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
          if (self.has(btn.getAttribute('data-product-handle'))) {
            btn.classList.add('is-wishlisted');
            btn.setAttribute('aria-pressed', 'true');
          } else {
            btn.setAttribute('aria-pressed', 'false');
          }
        });
        // Also sync product page wishlist toggle button (kept for backwards compatibility)
        document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
          var section = btn.closest('[data-product-id]');
          if (!section) return;
          var json = section.querySelector('[data-product-json]');
          if (!json) return;
          try {
            var prod = JSON.parse(json.textContent);
            if (self.has(prod.handle)) {
              btn.classList.add('is-wishlisted');
              btn.setAttribute('aria-pressed', 'true');
            } else {
              btn.setAttribute('aria-pressed', 'false');
            }
          } catch (e) { /* ignore */ }
        });
        self.updateCount();
      }
    },

    /* =====================================================================
       3. Sticky Add-to-Cart Bar
       ===================================================================== */
    StickyCartBar: {
      init: function () {
        var bar = document.querySelector('[data-sticky-cart-bar]');
        if (!bar) return;
        var self = this;
        var form = document.querySelector('[data-add-to-cart]');
        var dismissed = false;

        if (form) {
          var observer = new IntersectionObserver(function (entries) {
            if (dismissed) return;
            entries.forEach(function (entry) {
              bar.classList.toggle('is-visible', !entry.isIntersecting);
              bar.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
            });
          }, { threshold: 0 });
          observer.observe(form);
        }

        // Add to cart click
        var addBtn = bar.querySelector('[data-sticky-add-to-cart]');
        if (addBtn) {
          addBtn.addEventListener('click', function () {
            var variantId = addBtn.getAttribute('data-variant-id');
            AliraTheme.CartDrawer.addToCart(parseInt(variantId, 10), 1);
          });
        }

        // Close
        var closeBtn = bar.querySelector('[data-sticky-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            dismissed = true;
            bar.classList.remove('is-visible');
          });
        }
      }
    },

    /* =====================================================================
       4. Notify Form (Back-in-Stock)
       ===================================================================== */
    NotifyForm: {
      init: function () {
        document.querySelectorAll('[data-notify-trigger]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var fields = btn.closest('[data-notify-form]').querySelector('[data-notify-fields]');
            if (fields) fields.classList.toggle('is-open');
          });
        });
      }
    },

    /* =====================================================================
       5. Scroll-to-Top
       ===================================================================== */
    ScrollToTop: {
      init: function () {
        var btn = document.getElementById('ScrollToTop');
        if (!btn) return;
        window.addEventListener('scroll', function () {
          btn.classList.toggle('is-visible', window.scrollY > 600);
        }, { passive: true });
        btn.addEventListener('click', function () {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
    },

    /* =====================================================================
       6. Cookie Consent Banner
       ===================================================================== */
    CookieBanner: {
      init: function () {
        var banner = document.getElementById('CookieBanner');
        if (!banner) return;
        if (this.getCookie('alira_cookie_consent')) return;

        var self = this;
        var mobileQuery = window.matchMedia('(max-width: 767px)');

        function syncOffset() {
          if (!mobileQuery.matches) {
            document.body.classList.remove('has-cookie-banner');
            document.documentElement.style.setProperty('--cookie-banner-height', '0px');
            return;
          }

          document.documentElement.style.setProperty('--cookie-banner-height', banner.offsetHeight + 'px');
          document.body.classList.add('has-cookie-banner');
        }

        function clearOffset() {
          document.body.classList.remove('has-cookie-banner');
          document.documentElement.style.setProperty('--cookie-banner-height', '0px');
          window.removeEventListener('resize', syncOffset);
        }

        setTimeout(function () {
          banner.classList.add('is-visible');
          syncOffset();
          window.addEventListener('resize', syncOffset);
        }, 2000);

        banner.querySelector('[data-cookie-accept]').addEventListener('click', function () {
          self.setCookie('alira_cookie_consent', 'accepted', 365);
          banner.classList.remove('is-visible');
          clearOffset();
        });
        banner.querySelector('[data-cookie-decline]').addEventListener('click', function () {
          self.setCookie('alira_cookie_consent', 'declined', 365);
          banner.classList.remove('is-visible');
          clearOffset();
        });
      },
      setCookie: function (name, value, days) {
        var d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax;Secure';
      },
      getCookie: function (name) {
        var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : '';
      }
    },

    /* =====================================================================
       7. Social Proof Badge
       ===================================================================== */
    SocialProof: {
      init: function () {
        var el = document.querySelector('[data-social-proof-text]');
        if (!el) return;
        var self = this;
        self.update(el);
        setInterval(function () { self.update(el); }, 8000);
      },
      update: function (el) {
        var sold = Math.floor(Math.random() * 18) + 3;
        var hours = Math.floor(Math.random() * 20) + 4;
        var template = window.cartStrings.soldInLastHours || '{sold} sold in last {hours} hours';
        el.textContent = template.replace('{sold}', sold).replace('{hours}', hours);
      }
    },

    /* =====================================================================
       8. Product Tabs
       ===================================================================== */
    ProductTabs: {
      init: function () {
        var tabsContainer = document.querySelector('[data-product-tabs]');
        if (!tabsContainer) return;
        var items = tabsContainer.querySelectorAll('.product-tabs__item');

        var syncPanel = function (item) {
          var panel = item.querySelector('[data-tab-panel]');
          var trigger = item.querySelector('[data-accordion-trigger]');
          if (!panel || !trigger) return;
          if (item.classList.contains('is-open')) {
            panel.classList.add('is-open');
            panel.style.maxHeight = panel.scrollHeight + 'px';
            trigger.setAttribute('aria-expanded', 'true');
          } else {
            panel.classList.remove('is-open');
            panel.style.maxHeight = '0px';
            trigger.setAttribute('aria-expanded', 'false');
          }
        };

        items.forEach(function (item) {
          syncPanel(item);
          var trigger = item.querySelector('[data-accordion-trigger]');
          if (!trigger) return;

          trigger.addEventListener('click', function () {
            var isOpen = item.classList.contains('is-open');
            items.forEach(function (other) {
              other.classList.remove('is-open');
              syncPanel(other);
            });

            if (!isOpen) {
              item.classList.add('is-open');
              syncPanel(item);
            }
          });
        });

        window.addEventListener('resize', function () {
          items.forEach(syncPanel);
        });
      }
    },

    /* =====================================================================
       9. Recently Viewed Products (localStorage)
       ===================================================================== */
    RecentlyViewed: {
      storageKey: 'alira_recently_viewed',
      init: function () {
        var section = document.querySelector('[data-recently-viewed]');
        if (!section) return;
        var currentHandle = section.getAttribute('data-current-handle');
        if (currentHandle) this.trackProduct(currentHandle);
        this.renderGrid(section, currentHandle);
      },
      trackProduct: function (handle) {
        var list = this.getAll().filter(function (h) { return h !== handle; });
        list.unshift(handle);
        if (list.length > 8) list = list.slice(0, 8);
        localStorage.setItem(this.storageKey, JSON.stringify(list));
      },
      getAll: function () {
        try { return JSON.parse(localStorage.getItem(this.storageKey)) || []; } catch (e) { return []; }
      },
      renderGrid: function (section, currentHandle) {
        var grid = section.querySelector('[data-recently-viewed-grid]');
        if (!grid) return;
        var handles = this.getAll().filter(function (h) { return h !== currentHandle; }).slice(0, 4);
        if (handles.length === 0) { section.style.display = 'none'; return; }

        var promises = handles.map(function (handle) {
          return fetch('/products/' + handle + '.js').then(function (r) { return r.json(); }).catch(function () { return null; });
        });

        Promise.all(promises).then(function (products) {
          products.forEach(function (p) {
            if (!p) return;
            var img = p.featured_image ? p.featured_image : '';
            var price = (p.price / 100).toLocaleString('en-IN');
            var card = document.createElement('a');
            card.href = p.url;
            card.className = 'product-card';

            var imgWrapper = document.createElement('div');
            imgWrapper.className = 'product-card__image-wrapper';
            var imgEl = document.createElement('img');
            imgEl.src = img;
            imgEl.alt = p.title;
            imgEl.className = 'product-card__image';
            imgEl.loading = 'lazy';
            imgEl.width = 300;
            imgEl.height = 300;
            imgWrapper.appendChild(imgEl);

            var info = document.createElement('div');
            info.className = 'product-card__info';
            var h3 = document.createElement('h3');
            h3.className = 'product-card__title';
            h3.textContent = p.title;
            var priceDiv = document.createElement('div');
            priceDiv.className = 'product-card__price';
            priceDiv.textContent = '\u20B9 ' + price;
            info.appendChild(h3);
            info.appendChild(priceDiv);

            card.appendChild(imgWrapper);
            card.appendChild(info);
            grid.appendChild(card);
          });
        });
      }
    },

    /* =====================================================================
       10. Newsletter Popup Modal
       ===================================================================== */
    NewsletterModal: {
      init: function () {
        var modal = document.getElementById('NewsletterModal');
        if (!modal) return;
        if (this.getCookie('alira_newsletter_seen')) return;

        var self = this;
        var prevFocus = null;
        setTimeout(function () {
          prevFocus = document.activeElement;
          modal.classList.add('is-open');
          document.body.classList.add('overflow-hidden');
          var firstInput = modal.querySelector('input, button');
          if (firstInput) firstInput.focus();
        }, 15000);

        var close = function () {
          modal.classList.remove('is-open');
          document.body.classList.remove('overflow-hidden');
          self.setCookie('alira_newsletter_seen', '1', 30);
          if (prevFocus) prevFocus.focus();
        };

        modal.querySelector('[data-newsletter-close]').addEventListener('click', close);
        modal.addEventListener('click', function (e) {
          if (e.target === modal) close();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
        });

        // Mark as seen on successful form submit too
        var form = modal.querySelector('form');
        if (form) {
          form.addEventListener('submit', function () {
            self.setCookie('alira_newsletter_seen', '1', 30);
          });
        }
      },
      setCookie: function (name, value, days) {
        var d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax;Secure';
      },
      getCookie: function (name) {
        var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : '';
      }
    },

    /* =====================================================================
       11. Image Zoom (click-to-zoom)
       ===================================================================== */
    ImageZoom: {
      init: function () {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        var mainImage = document.querySelector('[data-product-main-image]');
        if (!mainImage) return;
        var img = mainImage.querySelector('.product-details__image');
        if (!img) return;

        mainImage.addEventListener('click', function (e) {
          if (img.classList.contains('is-zoomed')) {
            img.classList.remove('is-zoomed');
            img.style.transformOrigin = '';
          } else {
            var rect = mainImage.getBoundingClientRect();
            var x = ((e.clientX - rect.left) / rect.width) * 100;
            var y = ((e.clientY - rect.top) / rect.height) * 100;
            img.style.transformOrigin = x + '% ' + y + '%';
            img.classList.add('is-zoomed');
          }
        });

        mainImage.addEventListener('mousemove', function (e) {
          if (!img.classList.contains('is-zoomed')) return;
          window.requestAnimationFrame(function() {
            var rect = mainImage.getBoundingClientRect();
            var x = ((e.clientX - rect.left) / rect.width) * 100;
            var y = ((e.clientY - rect.top) / rect.height) * 100;
            img.style.transformOrigin = x + '% ' + y + '%';
          });
        }, { passive: true });
      }
    },

    /* =====================================================================
       12. Share Button
       ===================================================================== */
    ShareButton: {
      init: function () {
        document.querySelectorAll('[data-copy-link]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            navigator.clipboard.writeText(window.location.href).then(function () {
              var feedback = btn.closest('.share-button').querySelector('[data-copy-feedback]');
              if (feedback) {
                feedback.classList.add('is-visible');
                setTimeout(function () { feedback.classList.remove('is-visible'); }, 2000);
              }
            });
          });
        });
      }
    },

    /* =====================================================================
       13. Quick View Modal
       ===================================================================== */
    QuickView: {
      init: function () {
        var modal = document.querySelector('[data-quick-view-modal]');
        if (!modal) return;
        var body = modal.querySelector('[data-quick-view-body]');
        var self = this;

        document.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-quick-view]');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          var url = btn.getAttribute('data-product-url');
          if (url && url.startsWith('/')) self.load(url, modal, body);
        });

        modal.querySelector('[data-quick-view-close]').addEventListener('click', function () {
          self.close(modal);
        });
        modal.addEventListener('click', function (e) {
          if (e.target === modal) self.close(modal);
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && modal.classList.contains('is-open')) self.close(modal);
        });
      },
      load: function (url, modal, body) {
        body.innerHTML = '<div style="padding:3rem;text-align:center"><div class="loading-spinner"></div></div>';
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        this._previousFocus = document.activeElement;

        var self = this;
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var productSection = doc.querySelector('.product-details');
            if (productSection) {
              var img = productSection.querySelector('.product-details__image');
              var title = productSection.querySelector('.product-details__title');
              var price = productSection.querySelector('.product-details__price');
              var desc = doc.querySelector('.product-tabs__panel[data-tab-panel="description"]');

              var imgSrc = img ? img.getAttribute('src') : '';
              var titleText = title ? title.textContent : '';
              var priceText = price ? price.textContent : '';

              var imgEl = document.createElement('img');
              imgEl.className = 'quick-view-modal__image';
              imgEl.src = imgSrc;
              imgEl.alt = titleText;

              var infoDiv = document.createElement('div');
              infoDiv.className = 'quick-view-modal__info';

              var h3 = document.createElement('h3');
              h3.className = 'quick-view-modal__title';
              h3.id = 'QuickViewTitle';
              h3.textContent = titleText;
              modal.setAttribute('aria-labelledby', 'QuickViewTitle');

              var priceDiv = document.createElement('div');
              priceDiv.className = 'quick-view-modal__price';
              // Check if the price element contains a dynamic-price span with data-inr
              var dynSpan = price ? price.querySelector('.dynamic-price[data-inr]') : null;
              if (dynSpan) {
                var inrVal = parseInt(dynSpan.getAttribute('data-inr'));
                if (!isNaN(inrVal)) {
                  priceDiv.textContent = '\u20B9 ' + inrVal.toLocaleString('en-IN');
                } else {
                  priceDiv.textContent = priceText;
                }
              } else {
                priceDiv.textContent = priceText;
              }

              var descDiv = document.createElement('div');
              descDiv.className = 'quick-view-modal__desc';
              if (desc) descDiv.textContent = desc.textContent.substring(0, 300);

              var linkEl = document.createElement('a');
              linkEl.href = url;
              linkEl.className = 'quick-view-modal__btn';
              linkEl.textContent = window.cartStrings.viewFullDetails || 'View Full Details';

              infoDiv.appendChild(h3);
              infoDiv.appendChild(priceDiv);
              infoDiv.appendChild(descDiv);
              infoDiv.appendChild(linkEl);

              body.innerHTML = '';
              body.appendChild(imgEl);
              body.appendChild(infoDiv);
              var focusTarget = modal.querySelector('[data-quick-view-close]');
              if (focusTarget) focusTarget.focus();
            }
          })
          .catch(function () {
            body.textContent = window.cartStrings.quickViewError || 'Could not load product.';
          });
      },
      close: function (modal) {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        if (this._previousFocus) this._previousFocus.focus();
      }
    },

    /* =====================================================================
       14. Product Recommendations
       ===================================================================== */
    ProductRecommendations: {
      init: function () {
        var section = document.querySelector('[data-product-recommendations]');
        if (!section) return;
        var url = section.getAttribute('data-url');
        if (!url || !url.startsWith('/')) return;

        // Lazy-load: only fetch when section scrolls into view
        var observer = new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          obs.unobserve(section);

          fetch(url)
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var parser = new DOMParser();
              var doc = parser.parseFromString(html, 'text/html');
              var inner = doc.querySelector('.product-recommendations');
              if (inner && inner.innerHTML.trim()) {
                section.innerHTML = inner.innerHTML;
                AliraTheme.Wishlist.syncButtons();
              }
            })
            .catch(function () { /* silent */ });
        }, { rootMargin: '0px 0px 200px 0px' });

        observer.observe(section);
      }
    },

    /* =====================================================================
       15. Free Shipping Bar
       ===================================================================== */
    FreeShippingBar: {
      init: function () {
        // Handled by Liquid re-render in Cart Drawer
      }
    },

    /* =====================================================================
       16. Gift Wrapping
       ===================================================================== */
    GiftWrapping: {
      init: function () {
        document.addEventListener('change', function (e) {
          if (e.target.matches('[data-gift-wrap-toggle]')) {
            var checked = e.target.checked;
            var variantId = e.target.getAttribute('data-gift-wrap-variant');
            if (!variantId) return;
            if (checked) {
              AliraTheme.CartDrawer.addToCart(parseInt(variantId, 10), 1);
            } else {
              // Remove gift wrapping by setting quantity to 0 via cart update
              fetch(window.routes.cart_update_url + '.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: { [variantId]: 0 } })
              })
                .then(function () { AliraTheme.CartDrawer.refreshDrawer(); })
                .catch(function (err) { console.error('Gift wrap remove error:', err); });
            }
          }
        });
      }
    },

    /* =====================================================================
       17. Shipping Calculator
       ===================================================================== */
    ShippingCalculator: {
      init: function () {
        document.addEventListener('click', function (e) {
          var toggleBtn = e.target.closest('[data-shipping-calculator-toggle]');
          if (toggleBtn) {
            var content = toggleBtn.closest('[data-shipping-calculator]').querySelector('.shipping-calculator__content');
            content.hidden = !content.hidden;
          }
          var submitBtn = e.target.closest('[data-submit-shipping]');
          if (submitBtn) {
            var container = submitBtn.closest('[data-shipping-calculator]');
            var country = container.querySelector('[data-address-country]').value;
            var zip = container.querySelector('[data-address-zip]').value;
            var province = container.querySelector('[data-address-province]').value;
            var response = container.querySelector('[data-shipping-response]');
            // Build response with DOM API to avoid innerHTML
            response.textContent = '';
            var p = document.createElement('p');
            p.className = 'shipping-calculator__success';
            p.textContent = window.cartStrings.standardShipping || 'Standard Shipping: Free';
            response.appendChild(p);
          }
        });
      }
    },

    /* =====================================================================
       18. Inventory Status
       ===================================================================== */
    InventoryStatus: {
      init: function () {
        // Basic pulse effect handled by CSS
        // Advanced variant listening relies on ProductForm events (not implemented here)
      }
    },

    /* =====================================================================
       19. Countdown Timer
       ===================================================================== */
    CountdownTimer: {
      init: function () {
        var timer = document.querySelector('[data-countdown-timer]');
        if (!timer) return;
        var endTime = new Date(timer.dataset.endTime).getTime();

        function update() {
          var now = new Date().getTime();
          var distance = endTime - now;
          if (distance < 0) {
            timer.style.display = 'none';
            return;
          }
          var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          var seconds = Math.floor((distance % (1000 * 60)) / 1000);

          timer.querySelector('[data-hours]').textContent = String(hours).padStart(2, '0');
          timer.querySelector('[data-minutes]').textContent = String(minutes).padStart(2, '0');
          timer.querySelector('[data-seconds]').textContent = String(seconds).padStart(2, '0');
        }
        setInterval(update, 1000);
        update();
      }
    },

    /* =====================================================================
       Dynamic Price INR Formatting
       Formats all .js-inr and .js-inr-btn elements with Indian locale
       ===================================================================== */
    DynamicPriceFormat: {
      init: function () {
        this.formatAll();
      },
      formatAll: function () {
        document.querySelectorAll('.js-inr').forEach(function (el) {
          var v = parseInt(el.getAttribute('data-inr'));
          if (!isNaN(v)) el.textContent = '\u20B9 ' + v.toLocaleString('en-IN');
        });
        document.querySelectorAll('.js-inr-btn').forEach(function (el) {
          var v = parseInt(el.getAttribute('data-inr'));
          var label = el.getAttribute('data-label') || '';
          if (!isNaN(v)) el.textContent = label + ' - \u20B9 ' + v.toLocaleString('en-IN');
        });
      }
    },

    /* =====================================================================
       Toast Notifications
       ===================================================================== */
    Toast: {
      container: null,

      getContainer: function () {
        if (!this.container) {
          this.container = document.createElement('div');
          this.container.className = 'toast-container';
          document.body.appendChild(this.container);
        }
        return this.container;
      },

      show: function (message, type) {
        type = type || 'success';
        var container = this.getContainer();
        var toast = document.createElement('div');
        toast.className = 'toast toast--' + type;
        toast.textContent = message;
        container.appendChild(toast);

        setTimeout(function () {
          toast.style.opacity = '0';
          toast.style.transition = 'opacity 0.3s ease';
          setTimeout(function () {
            toast.remove();
          }, 300);
        }, 3000);
      }
    }
  };

  // Auto-initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      AliraTheme.init();
    });
  } else {
    AliraTheme.init();
  }

  // Gift card copy code (CSP-safe, replaces inline onclick)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-copy-code]');
    if (!btn) return;
    var targetId = btn.getAttribute('data-copy-code');
    var el = document.getElementById(targetId);
    if (el && navigator.clipboard) navigator.clipboard.writeText(el.textContent.trim());
  });

  /* =========================================================================
     Wishlist Page Renderer
     Reads localStorage and builds the product grid on /pages/wishlist
     ========================================================================= */
  var wishlistGrid = document.querySelector('[data-wishlist-page]');
  if (wishlistGrid) {
    (function () {
      var STORAGE_KEY = 'alira_wishlist';
      var strings = window.cartStrings || {};

      function getItems() {
        try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch (e) { return []; }
      }

      function removeItem(handle) {
        var updated = getItems().filter(function (p) { return p.handle !== handle; });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        if (AliraTheme.Wishlist) AliraTheme.Wishlist.updateCount();
        render();
      }

      function formatMoney(cents) {
        if (!cents) return '';
        // price stored as money string already (e.g. "₹ 4,500")
        return cents;
      }

      function buildCard(product) {
        var article = document.createElement('article');
        article.className = 'product-card';

        // Image link
        var linkImg = document.createElement('a');
        linkImg.href = product.url || '#';
        linkImg.className = 'product-card__link';
        var label = (product.title || '').replace(/"/g, '&quot;');
        linkImg.setAttribute('aria-label', product.title || '');

        if (product.image) {
          var imgWrapper = document.createElement('div');
          imgWrapper.className = 'product-card__image-wrapper';
          var img = document.createElement('img');
          img.src = product.image;
          img.alt = product.title || '';
          img.className = 'product-card__image';
          img.loading = 'lazy';
          img.width = 500;
          img.height = 500;
          imgWrapper.appendChild(img);
          linkImg.appendChild(imgWrapper);
        }
        article.appendChild(linkImg);

        // Info
        var info = document.createElement('div');
        info.className = 'product-card__info';

        var titleLink = document.createElement('a');
        titleLink.href = product.url || '#';
        titleLink.className = 'product-card__title-link';
        var h3 = document.createElement('h3');
        h3.className = 'product-card__title';
        h3.textContent = product.title || '';
        titleLink.appendChild(h3);
        info.appendChild(titleLink);

        if (product.price) {
          var priceDiv = document.createElement('div');
          priceDiv.className = 'product-card__price';
          var priceSpan = document.createElement('span');
          priceSpan.className = 'product-card__price-current';
          priceSpan.textContent = formatMoney(product.price);
          priceDiv.appendChild(priceSpan);
          info.appendChild(priceDiv);
        }

        // Remove button
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'wishlist-page__remove-btn';
        removeBtn.setAttribute('aria-label', (strings.removeFromWishlist || 'Remove from wishlist') + ': ' + (product.title || ''));
        removeBtn.dataset.handle = product.handle;
        removeBtn.textContent = '\u00D7'; // ×
        removeBtn.addEventListener('click', function () {
          removeItem(product.handle);
        });
        info.appendChild(removeBtn);

        article.appendChild(info);
        return article;
      }

      function render() {
        var items = getItems();
        wishlistGrid.innerHTML = '';

        if (items.length === 0) {
          var emptyDiv = document.createElement('div');
          emptyDiv.className = 'wishlist-page__empty';
          var emptyP = document.createElement('p');
          emptyP.textContent = strings.wishlistEmpty || 'Your wishlist is empty.';
          var cta = document.createElement('a');
          cta.href = '/collections/all';
          cta.className = 'btn btn--primary';
          cta.textContent = strings.continueShopping || 'Continue Shopping';
          emptyDiv.appendChild(emptyP);
          emptyDiv.appendChild(cta);
          wishlistGrid.appendChild(emptyDiv);
          return;
        }

        var grid = document.createElement('div');
        grid.className = 'product-grid';
        grid.style.setProperty('--columns', '4');
        grid.style.setProperty('--columns-mobile', '2');

        items.forEach(function (product) {
          grid.appendChild(buildCard(product));
        });

        wishlistGrid.appendChild(grid);
      }

      render();
    })();
  }

  /* =========================================================================
     Sold-Out Badge Hydration for Collection / Listing Pages
     Fetches fresh availability from /products/{handle}.js (uncached) for each
     card that has a sold-out badge, and removes the badge if the product is
     actually purchasable. Does NOT update price — Liquid-rendered dynamic prices
     are always accurate (metafields + gold rate at render time).
     ========================================================================= */
  (function () {
    var soldOutCards = Array.from(
      document.querySelectorAll('[data-product-card][data-product-handle]')
    ).filter(function (card) {
      return card.querySelector('.product-card__badge--sold-out') !== null;
    });

    if (soldOutCards.length === 0) return;

    soldOutCards.forEach(function (card) {
      var handle = card.getAttribute('data-product-handle');
      if (!handle) return;

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (fresh) {
          var isAvailable = fresh.variants.some(function (v) { return v.available; });
          if (isAvailable) {
            var badge = card.querySelector('.product-card__badge--sold-out');
            if (badge) badge.remove();
            var quickAdd = card.querySelector('[data-quick-add]');
            if (quickAdd) quickAdd.style.display = '';
          }
        })
        .catch(function () { /* silent fail */ });
    });
  })();

})();
